/**
 * Notification Bars List Component
 * 
 * @package TopBuddy
 */

import { useState, useEffect } from '@wordpress/element';
import { Button, Spinner } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import NotificationBarsTable from './NotificationBarsTable';
import EmptyState from './EmptyState';

const NotificationBarsList = () => {
	const [posts, setPosts] = useState([]);
	const [activeBarIds, setActiveBarIds] = useState([]);
	const [isPro, setIsPro] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		const fetchNotificationBars = async () => {
			setLoading(true);
			setError(null);

			try {
				const data = await apiFetch({
					path: '/topbuddy/v1/notification-bars',
					method: 'GET',
				});

				setPosts(data.posts || []);
				setActiveBarIds(data.activeBarIds || []);
				setIsPro(data.isPro || false);
			} catch (err) {
				setError(err.message || __('Failed to load notification bars', 'topbuddy'));
				console.error('Error fetching notification bars:', err);
			} finally {
				setLoading(false);
			}
		};

		fetchNotificationBars();
	}, []);

	const handleToggle = (postId, newActiveBarIds) => {
		// Update the active bar IDs based on the response
		setActiveBarIds(newActiveBarIds);
	};

	const handleDelete = async (postId) => {
		if (!confirm(__('Are you sure you want to delete this notification bar?', 'topbuddy'))) {
			return;
		}

		try {
			await apiFetch({
				path: `/wp/v2/topbuddy_nb/${postId}`,
				method: 'DELETE',
			});

			// Remove the post from the local state
			setPosts(posts.filter(post => post.id !== postId));

			// If the deleted post was active, remove it from active bars
			if (activeBarIds.includes(postId)) {
				setActiveBarIds(activeBarIds.filter(id => id !== postId));
			}
		} catch (err) {
			alert(err.message || __('Failed to delete notification bar', 'topbuddy'));
			console.error('Error deleting notification bar:', err);
		}
	};

	const handleDuplicate = async (postId) => {
		try {
			const duplicatedPost = await apiFetch({
				path: `/topbuddy/v1/notification-bars/${postId}/duplicate`,
				method: 'POST',
			});

			// Add new post to the beginning of the list
			setPosts([duplicatedPost, ...posts]);
		} catch (err) {
			console.error('Error duplicating notification bar:', err);
			alert(err.message || __('Failed to duplicate notification bar', 'topbuddy'));
		}
	};

	if (loading) {
		return (
			<div className="topbuddy-loading">
				<Spinner />
				<p>{__('Loading notification bars...', 'topbuddy')}</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className="notice notice-error">
				<p>{error}</p>
			</div>
		);
	}

	if (posts.length === 0) {
		return <EmptyState />;
	}

	return (
		<div className="topbuddy-notification-bars-list">
			<NotificationBarsTable
				posts={posts}
				activeBarIds={activeBarIds}
				isPro={isPro}
				onToggle={handleToggle}
				onDelete={handleDelete}
				onDuplicate={handleDuplicate}
			/>
		</div>
	);
};

export default NotificationBarsList;

